import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from '../../hooks/useTutorial';
import { WALKTHROUGH_STEPS } from './walkthroughSteps';

export function TutorialWalkthrough(): React.ReactElement | null {
  const { isActive, complete } = useTutorial();
  const [stepIndex, setStepIndex] = useState(0);
  const totalSteps = WALKTHROUGH_STEPS.length;
  const step = WALKTHROUGH_STEPS[stepIndex] ?? WALKTHROUGH_STEPS[0];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  const goNext = useCallback(() => {
    if (isLastStep) {
      complete();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
  }, [complete, isLastStep, totalSteps]);

  const goBack = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const next = WALKTHROUGH_STEPS[stepIndex + 1];
    if (!next?.image) return;
    const img = new Image();
    img.src = next.image;
  }, [isActive, stepIndex]);

  useEffect(() => {
    if (!isActive) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!isFirstStep) goBack();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, goNext, goBack, isFirstStep]);

  if (!isActive) {
    return null;
  }

  const node = (
    <div
      className="fixed inset-0 z-[60000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 pointer-events-auto animate-tutorial-overlay-in"
      style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="walkthrough-title"
      aria-describedby="walkthrough-desc"
    >
      <div className="flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-lg bg-white shadow-2xl animate-tutorial-tooltip-in">
        <div className="flex shrink-0 items-center gap-2 rounded-t-lg bg-[#1a3a2a] px-4 py-3 text-white">
          <p id="walkthrough-title" className="min-w-0 flex-1 text-lg font-semibold">
            TicketKoala Guide
          </p>
          <p className="shrink-0 text-xs text-white/70">
            Step {stepIndex + 1} of {totalSteps}
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {step.image && (
            <img src={step.image} alt={step.title} className="w-full rounded-lg mb-4" />
          )}
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            {step.title}
          </h2>
          <p
            id="walkthrough-desc"
            className="mt-1 text-sm text-gray-500 leading-relaxed"
          >
            {step.description}
          </p>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 px-5 py-4">
          {!isFirstStep && (
            <button
              type="button"
              onClick={goBack}
              className="border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-5 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={goNext}
            className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-md text-sm font-medium transition-colors shadow-sm"
          >
            {isLastStep ? 'Start Assessment' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
